import { ReactElement } from "react"
import { MarketplaceContractAddress } from "../enum/constants"

type ConfigState = {
	[key: string]: {
		normal: string | string[]
		isPreview?: string | string[]
		previewMobile?: string | string[]
	}
}

/** Shared banner type tokens — Figma sizes, fluid via clamp */
export const bannerType = {
	fonts: {
		konnect: "Konnect, sans-serif",
		inter: "Inter, sans-serif",
	},
	heroTitle: {
		fontSize: "clamp(48px, 4vw + 30px, 90px)",
		lineHeight: `${87 / 90}`, // 87px at 90px
	},
	heroTitleMobile: {
		fontSize: "clamp(28px, 8vw, 40px)",
		lineHeight: `${87 / 90}`,
	},
	midTitle: {
		fontSize: "clamp(22px, 1.2vw + 18px, 30px)",
	},
	midLeftTitleLineHeight: `${25 / 30}`, // 25px at 30px
	midRightTitleLineHeight: `${30.5 / 30}`, // 30.5px at 30px
	body: {
		fontSize: "clamp(11px, 0.2vw + 10.5px, 13px)",
		lineHeight: "clamp(16px, 0.3vw + 14.5px, 18px)",
	},
	longTitle: {
		fontSize: "clamp(36px, 2.5vw + 20px, 60px)",
		lineHeight: "clamp(28px, 1.5vw + 18px, 38px)",
	},
	longSubtitle: {
		fontSize: "clamp(20px, 1.2vw + 12px, 30px)",
		lineHeight: "clamp(28px, 1.5vw + 18px, 38px)",
	},
} as const

export const bannerHeroTitleProps = {
	fontFamily: bannerType.fonts.konnect,
	fontWeight: "700" as const,
	fontStyle: "normal" as const,
	fontSize: bannerType.heroTitle.fontSize,
	lineHeight: bannerType.heroTitle.lineHeight,
	letterSpacing: "0",
	textTransform: "uppercase" as const,
}

export const bannerHeroTitleMobileProps = {
	...bannerHeroTitleProps,
	fontSize: bannerType.heroTitleMobile.fontSize,
	lineHeight: bannerType.heroTitleMobile.lineHeight,
}

export const bannerBodyProps = {
	fontFamily: bannerType.fonts.inter,
	fontWeight: "400" as const,
	fontStyle: "normal" as const,
	fontSize: bannerType.body.fontSize,
	lineHeight: bannerType.body.lineHeight,
	letterSpacing: "0",
}

export const bannerMidTitleProps = {
	fontFamily: bannerType.fonts.konnect,
	fontWeight: "700" as const,
	fontStyle: "normal" as const,
	fontSize: bannerType.midTitle.fontSize,
	letterSpacing: "0",
}

export const bannerLongTitleProps = {
	fontFamily: bannerType.fonts.konnect,
	fontWeight: "700" as const,
	fontStyle: "normal" as const,
	fontSize: bannerType.longTitle.fontSize,
	lineHeight: bannerType.longTitle.lineHeight,
	letterSpacing: "0",
}

export const bannerLongSubtitleProps = {
	fontFamily: bannerType.fonts.konnect,
	fontWeight: "400" as const,
	fontStyle: "normal" as const,
	fontSize: bannerType.longSubtitle.fontSize,
	lineHeight: bannerType.longSubtitle.lineHeight,
	letterSpacing: "0",
}

export const headerBannerStyleConfig: ConfigState = {
	height: {
		normal: ["40vh", "85vh", "85vh"],
		previewMobile: "30vh"
	},
	width: {
		normal: "100%",
		isPreview: "100vw",
		previewMobile: "100%"
	},
	maxWidth: {
		normal: ["", "", "88%"],
		previewMobile: ""
	},
	marginLeft: {
		normal: "auto",
		isPreview: "",
		previewMobile: ""
	},
	backgroundSize: {
		normal: ["cover, cover", "cover, cover", "cover, cover"],
		previewMobile: "cover, cover"
	},
	backgroundPosition: {
		normal: ["center", "center, center", "center, center"],
		previewMobile: "center"
	},
	fontSizeTitle: {
		normal: bannerType.heroTitle.fontSize,
		previewMobile: "65px"
	},
	fontSizeDescription: {
		normal: bannerType.body.fontSize,
		previewMobile: "11px"
	}
}

export const longBannerStyleConfig: ConfigState = {
	height: {
		normal: ["300px", "340px"],
		previewMobile: "300px"
	},
	width: {
		normal: "100%",
		previewMobile: "100%"
	},
	maxWidth: {
		normal: ["100%", "100%", "88%"],
		previewMobile: "100%"
	},
	marginBottom: {
		normal: ["", "2rem"],
		previewMobile: ""
	},
	fontSizeTitle: {
		normal: bannerType.longTitle.fontSize,
		previewMobile: "3rem"
	},
	fontSizeDescription: {
		normal: bannerType.longSubtitle.fontSize,
		previewMobile: "1rem"
	},
	bottom: {
		normal: ["15%", "6%"],
		previewMobile: "15%"
	},
	right: {
		normal: ["50%", "5%"],
		previewMobile: "50%"
	},
	transform: {
		normal: ["translateX(50%)", "none"],
		previewMobile: "translateX(50%)"
	},
	backgroundPosition: {
		normal: ["center", "center", "left"],
		previewMobile: "center"
	},
	backgroundSize: {
		normal: ["cover, cover", "contain, cover"],
		previewMobile: "cover, cover"
	}
}

export const middleRightBannerStyleConfig: ConfigState = {
	position: {
		normal: ["", "", "absolute"],
		previewMobile: ""
	},
	flex: {
		normal: ["0 0 100%", "0 0 100%", "0 0 40%"],
		previewMobile: "0 0 100%"
	},
	height: {
		normal: ["300px", "300px", "80vh"],
		previewMobile: "300px"
	},
	bottom: {
		normal: ["auto", "auto", "15%"],
		previewMobile: "auto"
	},
	right: {
		normal: ["0", "0", "50%"],
		previewMobile: "0"
	},
	transform: {
		normal: ["", "translate(12.5%, 0%)", "translateX(50%)"],
		previewMobile: ""
	},
	fontSize: {
		normal: bannerType.body.fontSize,
		previewMobile: bannerType.body.fontSize
	},
	fontSizeTitle: {
		normal: bannerType.midTitle.fontSize,
		previewMobile: bannerType.midTitle.fontSize
	},
	fontSizeDescription: {
		normal: bannerType.body.fontSize,
		previewMobile: bannerType.body.fontSize
	},
	textAlign: {
		normal: ["center", "center", "center"],
		previewMobile: "center"
	},
	width: {
		normal: ["80%", "80%", "80%"],
		previewMobile: "80%"
	},
	maxWidth: {
		normal: ["100%", "100%", "100%"],
		previewMobile: "100%"
	},
	margin: {
		normal: ["auto 0", "auto 0", "auto"],
		previewMobile: "auto 0"
	},
	padding: {
		normal: ["10px 0px", "10px 0px", "10px 0px"],
		previewMobile: "10px 0px"
	}
}

export const middleLeftBannerConfigStyles: ConfigState = {
	flex: {
		normal: ["0 0 100%", "0 0 100%", "0 0 58%"],
		previewMobile: "0 0 100%"
	},
	height: {
		normal: ["300px", "300px", "80vh"],
		previewMobile: "300px"
	},
	textAlign: {
		normal: ["center", "center", "end"],
		previewMobile: "center"
	},
	right: {
		normal: ["", "", "4%"],
		previewMobile: ""
	},
	margin: {
		normal: ["auto 0", "auto 0", "auto"],
		previewMobile: "auto 0"
	},
	left: {
		normal: ["0", "0", "auto"],
		previewMobile: "0"
	},
	position: {
		normal: ["", "", "absolute"],
		previewMobile: ""
	},
	fontSizeTitle: {
		normal: bannerType.midTitle.fontSize,
		previewMobile: bannerType.midTitle.fontSize
	},
	fontSizeDescription: {
		normal: bannerType.body.fontSize,
		previewMobile: bannerType.body.fontSize
	}
}

type ConfigStates = {
	[key: string]: boolean
}

export function getBannerStyle(
	configSet: ConfigState,
	key: string,
	configStates: ConfigStates
): string | string[] {
	const styles = configSet[key]
	// If 'isPreview' is true, return the matching style or fall back to 'normal'
	if (configStates.isPreview) {
		return styles?.isPreview ?? styles.normal
	}

	// If 'previewMobile' is true, return the matching style.
	// (Assuming 'previewMobile' will always have a value as per your requirement)
	if (configStates.previewMobile) {
		return styles.previewMobile
	}

	// If neither 'isPreview' nor 'previewMobile' are true, return the 'normal' style
	return styles.normal
}

export function textAreaToSpans(text: string): ReactElement {
	// Block lines — Chakra Text can ignore <br />, which glued "METALOOKZ\nBLIND" into one word
	return (
		<>
			{(text ?? "").split(/\r?\n|\u2028/).map((item, i) => (
				<span key={i} style={{ display: "block" }}>
					{item}
				</span>
			))}
		</>
	)
}

export const fashionverseWebImagesCdnBaseUrl =
	process.env.NEXT_PUBLIC_FASHIONVERSE_WEB_IMAGES_CDN_BASE_URL

export const getImageUrl = (file: string | File | Blob): string | null => {
	// Regular expression to test if the string is a valid URL
	const urlPattern = new RegExp(
		"^(https?:\\/\\/)?" + // protocol
			"((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
			"((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
			"(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
			"(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
			"(\\#[-a-z\\d_]*)?$",
		"i"
	) // fragment locator

	if (file === null || file === undefined) {
		return null
	} else if (typeof file === "string") {
		// Already-absolute path served from the same origin (e.g. "/static/box1.png")
		if (file.startsWith("/")) {
			return file
		}
		// Check if the string is a valid URL
		if (urlPattern.test(file)) {
			// It's a valid URL. Return the URL itself.
			return file
		} else {
			// It's a path from the backend. Prepend the base URL of your CDN.
			return `${fashionverseWebImagesCdnBaseUrl}/${file}`
		}
	} else if (file instanceof File || file instanceof Blob) {
		// It's a file object from a file input. Create an object URL.
		return URL.createObjectURL(file)
	} else {
		// It's neither a path nor a file object.
		return null
	}
}

export const EditorialTypes = {
	Sporty: "sporty",
	Digi: "digi",
	AllBlack: "all-black-everything",
	RoarTigerstyle: "roar-tigerstyle",
	cmp: "cmp"
}

export const BannerTypes = {
	Header: "Header",
	MiddleLeft: "MiddleLeft",
	MiddleRight: "MiddleRight",
	LongBottom: "LongBottom",
	BoxHeader: "BoxHeader",
}

export const OldBannerTypes = {
	Header: "Header",
	Block_Large: "Block_Large",
	Block_Small: "Block_Small",
	Long_Banner: "Long_Banner",
	bg: "bg",
	hape: "hape",
	cmp: "cmp"
}

const marketplaceAwsBaseUrl = `https://d5fcg745fuvkn.cloudfront.net/fit-in/1920x1080`

const getEditorialUrl = (editorialType: string, bannerType: string) => {
	return `${marketplaceAwsBaseUrl}/editorial/${editorialType}/${bannerType}.png`
}

const getEditorialUrlClaimingSite = (bannerType: string) => {
	return `${marketplaceAwsBaseUrl}/editorial/claiming-site/${bannerType}.png`
}

const getEditorialUrlDressX = (bannerType: string) => {
	return `${marketplaceAwsBaseUrl}/editorial/dressx/${bannerType}.png`
}

export const EditorialBannerMapping = {
	DressX: {
		Header: getEditorialUrlDressX("isolated_hapes_cropped"),
		bg: getEditorialUrlDressX("BG"),
		Header_With_BG: getEditorialUrlDressX("Header"),
		Header_Title: <>HAPEX</>,
		Header_Description: (
			<>
				An exclusive collection of carefully curated pieces by <br />
				Hape and DRESSX. Be one of the few to own this <br />
				limited edition collection.
			</>
		)
	},
	ClaimingSite: {
		Header: getEditorialUrlClaimingSite("orange-hero"),
		Block_Large: getEditorialUrlClaimingSite("large-block"),
		Block_Small: getEditorialUrlClaimingSite("small-block"),
		Long_Banner: getEditorialUrlClaimingSite("long-banner"),
		Long_Banner_Mobile: getEditorialUrlClaimingSite("long-banner-image-only"),
		Header_Title: (
			<>
				Hape®2k23
				<br />
				SEASON ONE
			</>
		),
		Header_Description: (
			<>
				Exclusive Premium Streetwear Collection.
				<br />
				Street Fashion, Your Style. <b>Available Now.</b>{" "}
			</>
		),
		Long_Banner_Title: <>Hape®2k23 SEASON ONE</>,
		Long_Banner_Description: <>Premium Streetwear Collection</>,
		Block_Large_Title: (
			<>
				Hape®2k23
				<br />
				SEASON ONE
			</>
		),
		Block_Large_Description: (
			<>
				Exclusive Apparel Collection. <br />
				Street Fashion, Your Style.
			</>
		),
		Block_Small_Title: <>Hape®2k23 SEASON ONE</>,
		Block_Small_Title_Mobile: (
			<>
				Hape®2k23 <br />
				SEASON ONE
			</>
		),
		Block_Small_Description: <>Premium Streetwear Collection</>
	},
	Sporty: {
		bg: getEditorialUrl(EditorialTypes.Sporty, OldBannerTypes.bg),
		hape: getEditorialUrl(EditorialTypes.Sporty, OldBannerTypes.hape),
		Header: getEditorialUrl(EditorialTypes.Sporty, OldBannerTypes.Header),
		Block_Large: getEditorialUrl(
			EditorialTypes.Sporty,
			OldBannerTypes.Block_Large
		),
		Block_Small: getEditorialUrl(
			EditorialTypes.Sporty,
			OldBannerTypes.Block_Small
		),
		Long_Banner: getEditorialUrl(
			EditorialTypes.Sporty,
			OldBannerTypes.Long_Banner
		),
		Title: "GET SPORTY",
		Description: (
			<>
				The most important part of getting fit is the fit. <br /> Bring your
				A-game with this Hape collection, inspired by <br />
				designer sportswear.
			</>
		)
	},
	Digi: {
		bg: getEditorialUrl(EditorialTypes.Digi, OldBannerTypes.bg),
		hape: getEditorialUrl(EditorialTypes.Digi, OldBannerTypes.hape),
		Header: getEditorialUrl(EditorialTypes.Digi, OldBannerTypes.Header),
		Block_Large: getEditorialUrl(
			EditorialTypes.Digi,
			OldBannerTypes.Block_Large
		),
		Block_Small: getEditorialUrl(
			EditorialTypes.Digi,
			OldBannerTypes.Block_Small
		),
		Long_Banner: getEditorialUrl(
			EditorialTypes.Digi,
			OldBannerTypes.Long_Banner
		),
		Title: "DIGIVERSE",
		Description: (
			<>
				Inspired by Hape creator Digi, a curated collection of traits that{" "}
				<br /> will make your Hape feel like the <br /> king of the
				Fashionverse. Go primal.
			</>
		)
	},
	AllBlack: {
		bg: getEditorialUrl(EditorialTypes.AllBlack, OldBannerTypes.bg),
		hape: getEditorialUrl(EditorialTypes.AllBlack, OldBannerTypes.hape),
		Header: getEditorialUrl(EditorialTypes.AllBlack, OldBannerTypes.Header),
		Block_Large: getEditorialUrl(
			EditorialTypes.AllBlack,
			OldBannerTypes.Block_Large
		),
		Block_Small: getEditorialUrl(
			EditorialTypes.AllBlack,
			OldBannerTypes.Block_Small
		),
		Long_Banner: getEditorialUrl(
			EditorialTypes.AllBlack,
			OldBannerTypes.Long_Banner
		),
		Title: (
			<>
				ALL BLACK <br /> EVERYTHING
			</>
		),
		Description: (
			<>
				{
					"You know you've got something in your wardrobe that looks good with black,"
				}
				<br />
				{"because everything looks good with black. Especially black."}
			</>
		)
	},
	RoarTigerStyle: {
		bg: getEditorialUrl(EditorialTypes.RoarTigerstyle, OldBannerTypes.bg),
		hape: getEditorialUrl(EditorialTypes.RoarTigerstyle, OldBannerTypes.hape),
		Header: getEditorialUrl(
			EditorialTypes.RoarTigerstyle,
			OldBannerTypes.Header
		),
		Block_Large: getEditorialUrl(
			EditorialTypes.RoarTigerstyle,
			OldBannerTypes.Block_Large
		),
		Block_Small: getEditorialUrl(
			EditorialTypes.RoarTigerstyle,
			OldBannerTypes.Block_Small
		),
		Long_Banner: getEditorialUrl(
			EditorialTypes.RoarTigerstyle,
			OldBannerTypes.Long_Banner
		),
		Title: "ROAR LIKE A TIGER",
		Description: (
			<>
				{"There's more than one fashion icon in the jungle."}
				<br /> Make your Hape roar, tiger style.
			</>
		)
	},
	cmp: {
		bg: getEditorialUrl(EditorialTypes.cmp, OldBannerTypes.bg),
		hape: getEditorialUrl(EditorialTypes.cmp, OldBannerTypes.hape),
		isPrimary: true,
		Title: (
			<>
				GRAND <br /> OPENING
			</>
		),
		Description: (
			<>
				Become a part of Web3 history. Purchase traits with ETH or CMP, <br />
				customize the look of your Hape Prime NFT and be part of the future of
				fashion.
			</>
		),
		Description_Mobile: (
			<>
				Become a part of Web3 history. Purchase traits with ETH or CMP,
				customize the look of your Hape Prime NFT and be part of the future of
				fashion.
			</>
		)
	}
}

export const EditorialTypesDatabase = {
	Sporty: { name: "Sporty", bannerMapping: EditorialBannerMapping.Sporty },
	Digi: { name: "Digiverse", bannerMapping: EditorialBannerMapping.Digi },
	RoarTigerstyle: {
		name: "ROAR Tigerstyle",
		bannerMapping: EditorialBannerMapping.RoarTigerStyle
	},
	AllBlack: {
		name: "All Black Everything",
		bannerMapping: EditorialBannerMapping.AllBlack
	},
	ClaimingSite: {
		name: "Hape 2k23 SEASON ONE",
		bannerMapping: EditorialBannerMapping.ClaimingSite
	},
	DressX: {
		name: "HAPEX",
		bannerMapping: EditorialBannerMapping.DressX
	}
}

export const ClaimingSiteUrl = "https://apparel.hape.io"
export const DressXSiteUrl =
	"https://nft.dressx.com/marketplace/group/HapeX_Drop/34/0/"


export function parseDropAttributes(attributes: unknown): any[] {
	if (Array.isArray(attributes)) return attributes
	if (typeof attributes === "string" && attributes.trim()) {
		try {
			const parsed = JSON.parse(attributes)
			return Array.isArray(parsed) ? parsed : []
		} catch {
			return []
		}
	}
	return []
}

export function getTraitValue(traits:any, targetType:string) {
	const attrs = parseDropAttributes(traits)
	const target = attrs.find((trait: any) => trait.trait_type === targetType)
	return target ? target.value : null
}

/** Prefer the drop column over stale attributes.Rarity. */
export function resolveDropTier(drop: {
	rarity_type?: string | null
	tier_standard?: string | null
	attributes?: unknown
} | null | undefined): string {
	if (!drop) return ""
	return (
		drop.rarity_type ||
		drop.tier_standard ||
		getTraitValue(drop.attributes, "Rarity") ||
		""
	)
}

/** Fashion score from drop rarity_score, traits, or aggregated ratings. */
export function resolveDropFashionScore(drop: {
	attributes?: unknown
	rarity_score?: number | string | null
	avg_rating?: number | null
	rating?: number | null
} | null | undefined): number | null {
	if (!drop) return null

	const rarityScore = Number(drop.rarity_score)
	if (Number.isFinite(rarityScore)) return Math.round(rarityScore)

	const attrs = parseDropAttributes(drop.attributes)
	const fromTrait =
		getTraitValue(attrs, "FashionScore") ?? getTraitValue(attrs, "Fashion Score")
	if (fromTrait != null && fromTrait !== "") {
		const parsed = Number(fromTrait)
		if (Number.isFinite(parsed)) return Math.round(parsed)
	}

	const avgRating = Number(drop.avg_rating ?? 0)
	if (Number.isFinite(avgRating) && avgRating > 0) return Math.round(avgRating)

	const rating = Number(drop.rating ?? 0)
	if (Number.isFinite(rating) && rating > 0) return Math.round(rating)

	return null
}

const toStockCount = (value: unknown) => {
	const n = Number(value ?? 0)
	return Number.isFinite(n) ? n : 0
}

/** Treat missing stock as 0 — Go used to omit 0 via json omitempty. */
export function getProductStock(product: {
	stock_quantity?: number | null
	total_quantity?: number | null
} | null | undefined) {
	const remaining = toStockCount(product?.stock_quantity)
	const total = toStockCount(product?.total_quantity)
	return {
		remaining,
		total,
		isSoldOut: remaining < 1,
	}
}

/** Old ERC-721 marketplace catalog — listed, but not purchasable in Prime yet. */
export function isLegacyMarketplaceDrop(product: {
	contract_address?: string | null
} | null | undefined) {
	return (
		String(product?.contract_address || "").toLowerCase() ===
		MarketplaceContractAddress
	)
}

export function isDropPurchaseDisabled(product: {
	stock_quantity?: number | null
	total_quantity?: number | null
	contract_address?: string | null
} | null | undefined) {
	if (!product) return true
	return getProductStock(product).isSoldOut || isLegacyMarketplaceDrop(product)
}
